// src/pages/system/Roles.js
import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Security as SecurityIcon,
  VpnKey as PermissionIcon
} from '@mui/icons-material';
import RoleForm from './RoleForm';
import PermissionForm from './PermissionForm';
import DeleteConfirmDialog from '../../components/common/DeleteConfirmDialog';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Roles = () => {
  const [tabValue, setTabValue] = useState(0);

  // Estados para roles
  const [roles, setRoles] = useState([]);
  const [openRoleForm, setOpenRoleForm] = useState(false);
  const [selectedRole, setSelectedRole] = useState(null);
  const [roleDeleteDialogOpen, setRoleDeleteDialogOpen] = useState(false);
  const [roleToDelete, setRoleToDelete] = useState(null);

  // Estados para permisos
  const [permissions, setPermissions] = useState([]);
  const [openPermissionForm, setOpenPermissionForm] = useState(false);
  const [selectedPermission, setSelectedPermission] = useState(null);
  const [permissionDeleteDialogOpen, setPermissionDeleteDialogOpen] = useState(false);
  const [permissionToDelete, setPermissionToDelete] = useState(null);

  // Estados comunes
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchRoles();
    fetchPermissions();
  }, []);

  const fetchRoles = async () => {
    try {
      const response = await fetch('/api/roles', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al obtener roles');
      }

      const data = await response.json();
      setRoles(data);
    } catch (error) {
      console.error('Error fetching roles:', error);
      setError(error.message);
    }
  };

  const fetchPermissions = async () => {
    try {
      const response = await fetch('/api/roles/permissions', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al obtener permisos');
      }

      const data = await response.json();
      setPermissions(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching permissions:', error);
      setError(error.message);
      setLoading(false);
    }
  };

  // Funciones para roles
  const handleRoleSubmit = async (roleData) => {
    try {
      const url = selectedRole
        ? `/api/roles/${selectedRole.id}`
        : '/api/roles';

      const method = selectedRole ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(roleData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al guardar rol');
      }

      setOpenRoleForm(false);
      setSelectedRole(null);
      fetchRoles();
    } catch (error) {
      console.error('Error saving role:', error);
      setError(error.message);
    }
  };

  const handleRoleDelete = async () => {
    if (!roleToDelete) return;

    try {
      const response = await fetch(`/api/roles/${roleToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al eliminar rol');
      }

      setRoleDeleteDialogOpen(false);
      setRoleToDelete(null);
      fetchRoles();
    } catch (error) {
      console.error('Error deleting role:', error);
      setError(error.message);
    }
  };

  // Funciones para permisos
  const handlePermissionSubmit = async (permissionData) => {
    try {
      const url = selectedPermission
        ? `/api/roles/permissions/${selectedPermission.id}`
        : '/api/roles/permissions';

      const method = selectedPermission ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(permissionData)
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al guardar permiso');
      }

      setOpenPermissionForm(false);
      setSelectedPermission(null);
      fetchPermissions();
    } catch (error) {
      console.error('Error saving permission:', error);
      setError(error.message);
    }
  };

  const handlePermissionDelete = async () => {
    if (!permissionToDelete) return;

    try {
      const response = await fetch(`/api/roles/permissions/${permissionToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.message || 'Error al eliminar permiso');
      }

      setPermissionDeleteDialogOpen(false);
      setPermissionToDelete(null);
      fetchPermissions();
    } catch (error) {
      console.error('Error deleting permission:', error);
      setError(error.message);
    }
  };

  // Agrupar permisos por módulo
  const groupedPermissions = permissions.reduce((acc, permission) => {
    const module = permission.module || 'Sin módulo';
    if (!acc[module]) {
      acc[module] = [];
    }
    acc[module].push(permission);
    return acc;
  }, {});

  if (loading) {
    return (
      <Box className="flex justify-center items-center h-full">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="p-6">
      <Typography variant="h4" component="h1" className="mb-6">
        Roles y Permisos
      </Typography>

      {error && (
        <Alert
          severity="error"
          onClose={() => setError(null)}
          className="mb-4"
        >
          {error}
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab
            label="Roles"
            icon={<SecurityIcon />}
            iconPosition="start"
          />
          <Tab
            label="Permisos"
            icon={<PermissionIcon />}
            iconPosition="start"
          />
        </Tabs>
      </Box>

      {/* Panel de Roles */}
      <TabPanel value={tabValue} index={0}>
        <Box className="flex justify-between items-center mb-4">
          <Typography variant="h6">
            Gestión de Roles
          </Typography>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedRole(null);
              setOpenRoleForm(true);
            }}
          >
            Nuevo Rol
          </Button>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nombre</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell>Permisos</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell align="right">Acciones</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roles.map((role) => (
                <TableRow key={role.id}>
                  <TableCell>{role.name}</TableCell>
                  <TableCell>{role.description}</TableCell>
                  <TableCell>
                    <Box className="flex flex-wrap gap-1">
                      {(role.permissions || []).slice(0, 3).map((permission, index) => (
                        <Chip
                          key={index}
                          label={permission}
                          size="small"
                          color="primary"
                        />
                      ))}
                      {(role.permissions || []).length > 3 && (
                        <Chip
                          label={`+${role.permissions.length - 3}`}
                          size="small"
                          variant="outlined"
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={role.is_system ? 'Sistema' : 'Personalizado'}
                      color={role.is_system ? 'secondary' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box className="flex justify-end gap-2">
                      {!role.is_system && (
                        <>
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedRole(role);
                                setOpenRoleForm(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setRoleToDelete(role);
                                setRoleDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </TabPanel>

      {/* Panel de Permisos */}
      <TabPanel value={tabValue} index={1}>
        <Box className="flex justify-between items-center mb-4">
          <Typography variant="h6">
            Gestión de Permisos
          </Typography>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedPermission(null);
              setOpenPermissionForm(true);
            }}
          >
            Nuevo Permiso
          </Button>
        </Box>

        {Object.entries(groupedPermissions).map(([module, modulePermissions]) => (
          <Paper key={module} className="mb-4">
            <Box className="p-3 bg-gray-50">
              <Typography variant="h6" className="font-semibold">
                Módulo: {module}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {modulePermissions.length} permisos
              </Typography>
            </Box>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nombre</TableCell>
                    <TableCell>Código</TableCell>
                    <TableCell>Descripción</TableCell>
                    <TableCell align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {modulePermissions.map((permission) => (
                    <TableRow key={permission.id}>
                      <TableCell>{permission.name}</TableCell>
                      <TableCell>
                        <Chip
                          label={permission.code}
                          size="small"
                          variant="outlined"
                          color="primary"
                        />
                      </TableCell>
                      <TableCell>{permission.description}</TableCell>
                      <TableCell align="right">
                        <Box className="flex justify-end gap-1">
                          <Tooltip title="Editar">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedPermission(permission);
                                setOpenPermissionForm(true);
                              }}
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => {
                                setPermissionToDelete(permission);
                                setPermissionDeleteDialogOpen(true);
                              }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))}
      </TabPanel>

      {/* Formularios y Diálogos */}
      <RoleForm
        open={openRoleForm}
        onClose={() => {
          setOpenRoleForm(false);
          setSelectedRole(null);
        }}
        onSubmit={handleRoleSubmit}
        role={selectedRole}
      />

      <PermissionForm
        open={openPermissionForm}
        onClose={() => {
          setOpenPermissionForm(false);
          setSelectedPermission(null);
        }}
        onSubmit={handlePermissionSubmit}
        permission={selectedPermission}
      />

      <DeleteConfirmDialog
        open={roleDeleteDialogOpen}
        onClose={() => {
          setRoleDeleteDialogOpen(false);
          setRoleToDelete(null);
        }}
        onConfirm={handleRoleDelete}
        title="Eliminar Rol"
        content={`¿Está seguro de que desea eliminar el rol "${roleToDelete?.name}"?`}
      />

      <DeleteConfirmDialog
        open={permissionDeleteDialogOpen}
        onClose={() => {
          setPermissionDeleteDialogOpen(false);
          setPermissionToDelete(null);
        }}
        onConfirm={handlePermissionDelete}
        title="Eliminar Permiso"
        content={`¿Está seguro de que desea eliminar el permiso "${permissionToDelete?.name}"?`}
      />
    </Box>
  );
};

export default Roles;